/**
 * Global MusicKit auth-error swallower + CRA overlay killer.
 *
 * Apple MusicKit v3 fires "missing MusicUserToken" / AUTHORIZATION_ERROR
 * rejections from INSIDE its own async chain (`_prepareMediaAPIItem`).
 * Those bubble to `window.unhandledrejection` and — in dev — CRA's
 * `react-error-overlay` shows the scary red "Uncaught runtime errors" panel
 * even though the app itself has a clean recovery (toast + prompt reconnect).
 *
 * Two layers of defence:
 *   (1) Capture-phase `unhandledrejection` + `error` listeners that
 *       `preventDefault()` + `stopImmediatePropagation()` on any auth-
 *       flavoured error. This is enough in PRODUCTION.
 *   (2) DEV-only DOM sweep — `react-error-overlay` attaches its listener
 *       BEFORE our capture handler can fire (its client is loaded by
 *       webpack-dev-server's runtime chunk, which runs before our
 *       app bundle). We therefore can't beat it to the event; instead we
 *       remove its iframe from the DOM as soon as it appears, and set up
 *       a MutationObserver to remove it again on any re-render.
 *
 * The dispatched `spinitup:musickit-auth-error` CustomEvent lets
 * MusicContext hook in to unauthorize + toast without duplicating the
 * matching logic here.
 */

const isMusicKitAuthError = (msg) => {
  if (!msg) return false;
  const s = String(msg);
  return (
    /MusicUserToken/i.test(s) ||
    /AUTHORIZATION_ERROR/i.test(s) ||
    (/musickit/i.test(s) && /authoriz/i.test(s)) ||
    /NO_USER_TOKEN/i.test(s) ||
    /DEVELOPER_TOKEN_INVALID/i.test(s) ||
    /Unable to prepare media item/i.test(s)
  );
};

const stackPointsAtMusicKit = (err) => {
  const stack = err?.stack || err?.error?.stack || '';
  return /musickit(\.js)?/i.test(String(stack));
};

const shouldSwallow = (msg, err) => {
  if (isMusicKitAuthError(msg)) return true;
  if (err && stackPointsAtMusicKit(err) && /token|authoriz|prepare/i.test(String(msg))) return true;
  return false;
};

const dispatch = (msg) => {
  try {
    window.dispatchEvent(new CustomEvent('spinitup:musickit-auth-error', { detail: { msg } }));
  } catch { /* CustomEvent unavailable — ignore */ }
};

// --- DEV overlay killer -------------------------------------------------
// react-error-overlay renders inside a full-viewport fixed-position iframe
// appended to <body>. We locate it by z-index/style signature and remove
// it. A MutationObserver ensures re-renders also get removed.
const looksLikeCraOverlay = (node) => {
  if (!node || node.nodeType !== 1) return false;
  const tag = (node.tagName || '').toUpperCase();
  const id = node.id || '';
  if (id === 'webpack-dev-server-client-overlay') return true;
  if (id === 'react-refresh-overlay-error') return true;
  if (tag === 'IFRAME') {
    const style = node.getAttribute('style') || '';
    // react-error-overlay uses z-index: 2147483647 (max) and position:fixed
    if (/z-index:\s*2147483647/i.test(style) && /position:\s*fixed/i.test(style)) return true;
  }
  return false;
};

let overlaySuppressUntil = 0;
const killOverlayNow = () => {
  try {
    document.querySelectorAll('body > iframe, body > div').forEach((el) => {
      if (looksLikeCraOverlay(el)) {
        try { el.remove(); } catch { /* noop */ }
      }
    });
  } catch { /* noop */ }
};

const startOverlaySuppression = () => {
  // Kill immediately, then keep killing on every DOM mutation for the
  // next 5s — the overlay can re-render on subsequent MusicKit rejections
  // (Apple retries preparation several times before giving up).
  overlaySuppressUntil = Date.now() + 5000;
  killOverlayNow();
  // Also drop a stylesheet as a belt — even if we miss the node, the
  // overlay stays invisible while suppressed.
  try {
    let styleTag = document.getElementById('spinitup-musickit-overlay-suppress');
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = 'spinitup-musickit-overlay-suppress';
      styleTag.textContent = `
        body > iframe[style*="z-index: 2147483647"],
        body > iframe[style*="position: fixed"][style*="2147483647"],
        #webpack-dev-server-client-overlay,
        #react-refresh-overlay-error { display: none !important; }
      `;
      document.head.appendChild(styleTag);
    }
    // Remove the stylesheet after the suppression window elapses so real
    // future errors (unrelated to MusicKit) still surface normally.
    setTimeout(() => {
      if (Date.now() >= overlaySuppressUntil) {
        try { styleTag.remove(); } catch { /* noop */ }
      }
    }, 5200);
  } catch { /* noop */ }
};

let observerInstalled = false;
const installOverlayObserver = () => {
  if (observerInstalled) return;
  if (typeof MutationObserver === 'undefined' || !document.body) return;
  observerInstalled = true;
  const obs = new MutationObserver((records) => {
    if (Date.now() > overlaySuppressUntil) return;
    for (const r of records) {
      r.addedNodes && r.addedNodes.forEach((node) => {
        if (looksLikeCraOverlay(node)) {
          try { node.remove(); } catch { /* noop */ }
        }
      });
    }
  });
  obs.observe(document.body, { childList: true, subtree: false });
};

let installed = false;

export function installMusicKitErrorFilter() {
  if (installed) return;
  installed = true;

  const onUnhandled = (ev) => {
    const reason = ev?.reason;
    const msg = reason?.message || reason?.errorCode || reason?.name || String(reason || '');
    if (shouldSwallow(msg, reason)) {
      ev.preventDefault();
      if (typeof ev.stopImmediatePropagation === 'function') ev.stopImmediatePropagation();
      console.warn('[MusicKitErrorFilter] swallowed unhandledrejection:', msg);
      dispatch(msg);
      startOverlaySuppression();
    }
  };

  const onError = (ev) => {
    const msg = ev?.message || (ev?.error && (ev.error.message || String(ev.error))) || '';
    if (shouldSwallow(msg, ev?.error || ev)) {
      ev.preventDefault();
      if (typeof ev.stopImmediatePropagation === 'function') ev.stopImmediatePropagation();
      console.warn('[MusicKitErrorFilter] swallowed error event:', msg);
      dispatch(msg);
      startOverlaySuppression();
    }
  };

  // Capture phase — try to beat CRA's overlay listener.
  window.addEventListener('unhandledrejection', onUnhandled, true);
  window.addEventListener('error', onError, true);

  // Belt: mute console.error for MusicKit auth strings (CRA's overlay
  // ALSO monitors console.error in some builds).
  const origConsoleError = console.error.bind(console);
  console.error = (...args) => {
    const combined = args
      .map((a) => (typeof a === 'string' ? a : (a?.message || '')))
      .join(' ');
    if (shouldSwallow(combined, args.find((a) => a && typeof a === 'object'))) {
      console.warn('[MusicKitErrorFilter] muted console.error:', combined);
      dispatch(combined);
      startOverlaySuppression();
      return;
    }
    origConsoleError(...args);
  };

  // Wire up the DOM-removal observer once the body exists.
  if (document.body) {
    installOverlayObserver();
  } else {
    document.addEventListener('DOMContentLoaded', installOverlayObserver, { once: true });
  }
}

export default installMusicKitErrorFilter;
